import React from 'react';
import {
  RangoTronco,
  ModificadoresTronco,
  RangoCuello,
  ModificadoresCuello,
  CondicionPiernas,
  ModificadoresPiernas,
  RangoBrazo,
  ModificadoresBrazo,
  RangoAntebrazo,
  RangoMuneca,
  ModificadoresMuneca,
} from '../../lib/reba';
import {
  TRUNK_AXIS,
  LEGS_NEUTRAL,
  TORSO_STATIC,
  NECK_STATIC,
  HEAD_STATIC,
} from '../rula/RulaBodyDiagrams';

// ============================================================
// Diagramas de referencia REBA — mismo patrón visual que
// src/components/rula/RulaBodyDiagrams.tsx: silueta lateral 2D con
// volumen (piezas estáticas compartidas TRUNK_AXIS/LEGS_NEUTRAL/
// TORSO_STATIC/NECK_STATIC/HEAD_STATIC, importadas — no redefinidas),
// segmento medido resaltado en verde, arco/etiqueta de ángulo con
// fondo propio (pill). Tipos y rangos tomados de src/lib/reba.ts,
// NO de rula.ts.
//
// Geometría de arcos/ejes calculada analíticamente (mismo pivote y
// convención de rotación que sus contrapartes RULA — no son valores
// verificados en preview aislado con captura de navegador todavía;
// pendiente esa verificación visual antes de integrar a un formulario.
// ============================================================

function Pill({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return <rect x={x} y={y} width={w} height={h} rx={5} fill="#0b1220" opacity={0.9} stroke="#1e293b" strokeWidth={1} />;
}

const diagramSvgProps = { width: 260, height: 300, viewBox: '0 0 260 300' } as const;

// ===== Tronco =====
// El tronco es el segmento medido: rota junto con cuello+cabeza+brazo
// (atenuado) desde la cadera (110,193), igual que RulaTroncoDiagram.

const TRONCO_ANGLE: Record<RangoTronco, number> = {
  erguido: 5,
  flexion_extension_0_20: 15,
  flexion_20_60_o_extension_20: 40,
  flexion_mayor_60: 65,
};
const TRONCO_LABEL: Record<RangoTronco, string> = {
  erguido: 'erguido',
  flexion_extension_0_20: 'flexión/extensión 0°–20°',
  flexion_20_60_o_extension_20: 'flexión 20°–60° / extensión >20°',
  flexion_mayor_60: 'flexión >60°',
};
const TRONCO_LABEL_SIZE: Record<RangoTronco, number> = {
  erguido: 16,
  flexion_extension_0_20: 14,
  flexion_20_60_o_extension_20: 12,
  flexion_mayor_60: 16,
};
// eje/arco calculado con pivote en cadera (110,193): punto = cadera +
// R*(sin θ, -cos θ) — misma convención "hacia arriba" que Tronco en RULA.
const TRONCO_ARC: Record<RangoTronco, { line: [number, number]; arcD: string }> = {
  erguido: { line: [118.6, 94.4], arcD: 'M 110 148 A 45 45 0 0 1 113.9 148.2' },
  flexion_extension_0_20: { line: [135.6, 97.4], arcD: 'M 110 148 A 45 45 0 0 1 121.7 149.5' },
  flexion_20_60_o_extension_20: { line: [173.6, 117.2], arcD: 'M 110 148 A 45 45 0 0 1 138.9 158.5' },
  flexion_mayor_60: { line: [199.7, 151.2], arcD: 'M 110 148 A 45 45 0 0 1 150.8 174.0' },
};

export function RebaTroncoDiagram({ rango, mod }: { rango: RangoTronco; mod: ModificadoresTronco }) {
  const angle = TRONCO_ANGLE[rango];
  const { line, arcD } = TRONCO_ARC[rango];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      <g transform={`translate(110,193) rotate(${angle})`}>
        <path d="M -22,-99 C -27,-68 -23,-34 -12,2 L 24,2 C 34,-34 37,-68 32,-99 C 18,-89 -10,-89 -22,-99 Z" fill="#2FBF71" />
        <rect x={-7} y={-120} width={18} height={13} rx={4} fill="#e2e8f0" opacity={0.9} />
        <circle cx={0} cy={-137} r={21} fill="#e2e8f0" opacity={0.9} />
        <g transform="translate(0,-95) scale(1.3) rotate(-8)" opacity={0.55}>
          <rect x={-7} y={0} width={14} height={40} rx={7} fill="#e2e8f0" />
          <g transform="translate(0,40) rotate(-25)">
            <rect x={-6} y={0} width={12} height={34} rx={6} fill="#e2e8f0" />
            <circle cx={0} cy={36} r={7} fill="#e2e8f0" />
          </g>
        </g>
      </g>
      <line x1={110} y1={193} x2={line[0]} y2={line[1]} stroke="#2FBF71" strokeWidth={2} strokeDasharray="3 3" opacity={0.9} />
      <path d={arcD} stroke="#f59e0b" strokeWidth={3} fill="none" />

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={TRONCO_LABEL_SIZE[rango]} fontWeight={700} textAnchor="middle">{TRONCO_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje tronco</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">vertical</text>

      {mod.inclinacionLateralORotacion && (
        <>
          <path d="M 30,53 a 9,9 0 1 1 -3,-6" stroke="#3b82f6" strokeWidth={3} fill="none" strokeLinecap="round" />
          <path d="M 25,45 l 5,2 -2,5 Z" fill="#3b82f6" />
          <path d="M 17,95 L 43,95 M 23,89 L 17,95 L 23,101 M 37,89 L 43,95 L 37,101" stroke="#f59e0b" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
    </svg>
  );
}

// ===== Cuello =====
// La segunda categoría (flexión >20° o extensión) se ilustra como
// flexión pronunciada — combina dos direcciones opuestas en un solo
// puntaje, igual ambigüedad que RULA resuelve mostrando un solo caso
// representativo; el label aclara que también cubre extensión.

const CUELLO_ANGLE: Record<RangoCuello, number> = {
  flexion_0_20: 12,
  flexion_mayor_20_o_extension: 32,
};
const CUELLO_LABEL: Record<RangoCuello, string> = {
  flexion_0_20: 'flexión 0°–20°',
  flexion_mayor_20_o_extension: 'flexión >20° o extensión',
};
const CUELLO_LABEL_SIZE: Record<RangoCuello, number> = {
  flexion_0_20: 16,
  flexion_mayor_20_o_extension: 13,
};
// pivote en base del cuello (110,88), misma convención que Cuello RULA.
const CUELLO_ARC: Record<RangoCuello, { head: [number, number]; arcD: string }> = {
  flexion_0_20: { head: [116.7, 56.7], arcD: 'M 110 52 A 36 36 0 0 1 117.5 52.8' },
  flexion_mayor_20_o_extension: { head: [127.0, 60.9], arcD: 'M 110 52 A 36 36 0 0 1 129.1 57.5' },
};

export function RebaCuelloDiagram({ rango, mod }: { rango: RangoCuello; mod: ModificadoresCuello }) {
  const angle = CUELLO_ANGLE[rango];
  const { head, arcD } = CUELLO_ARC[rango];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-8)" opacity={0.55}>
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#e2e8f0" />
        <g transform="translate(0,40) rotate(-25)">
          <rect x={-6} y={0} width={12} height={34} rx={6} fill="#e2e8f0" />
          <circle cx={0} cy={36} r={7} fill="#e2e8f0" />
        </g>
      </g>
      <g transform={`translate(110,88) rotate(${angle})`}>
        <rect x={-9} y={-15} width={18} height={13} rx={4} fill="#2FBF71" />
        <circle cx={0} cy={-32} r={21} fill="#2FBF71" />
      </g>
      <line x1={110} y1={88} x2={head[0]} y2={head[1]} stroke="#2FBF71" strokeWidth={2} strokeDasharray="3 3" opacity={0.9} />
      <path d={arcD} stroke="#f59e0b" strokeWidth={3} fill="none" />

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={CUELLO_LABEL_SIZE[rango]} fontWeight={700} textAnchor="middle">{CUELLO_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje cuello</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">eje tronco</text>

      {mod.inclinacionLateralORotacion && (
        <>
          <path d="M 30,53 a 9,9 0 1 1 -3,-6" stroke="#3b82f6" strokeWidth={3} fill="none" strokeLinecap="round" />
          <path d="M 25,45 l 5,2 -2,5 Z" fill="#3b82f6" />
          <path d="M 17,95 L 43,95 M 23,89 L 17,95 L 23,101 M 37,89 L 43,95 L 37,101" stroke="#f59e0b" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
    </svg>
  );
}

// ===== Piernas =====
// A diferencia de RULA (piernas sin articulación), REBA puntúa la
// flexión de rodillas por separado -- cada pierna se dibuja como
// muslo+pantorrilla con articulación de rodilla (mismo patrón de
// "segmento anidado" que Brazo/Antebrazo), para poder mostrar el
// grado de flexión pedido en ModificadoresPiernas.flexionRodillas.

const KNEE_BEND: Record<'ninguna' | '30_60' | 'mayor_60', number> = {
  ninguna: 0,
  '30_60': 35,
  mayor_60: 65,
};
const PIERNAS_LABEL: Record<CondicionPiernas, string> = {
  soporte_bilateral_simetrico: 'soporte bilateral simétrico',
  soporte_unilateral_o_inestable: 'soporte unilateral / inestable',
};
const FLEXION_LABEL: Record<'ninguna' | '30_60' | 'mayor_60', string> = {
  ninguna: 'sin flexión de rodillas',
  '30_60': 'flexión de rodillas 30°–60°',
  mayor_60: 'flexión de rodillas >60°',
};

const LEG_THIGH_LEN = 38;
const LEG_SHIN_LEN = 38;
const LEG_W = 19;
const LEG_RX = 9;
const FOOT_RX = 13;
const FOOT_RY = 6.5;
// Distancia (a lo largo del eje local de la pantorrilla) desde la rodilla
// hasta el centro de la elipse del pie.
const FOOT_OFFSET = LEG_SHIN_LEN + FOOT_RY;
const GROUND_Y = 277;

// Cadera→rodilla→pie con articulación de rodilla (kneeBend gira la
// pantorrilla respecto a la rodilla). Con cadera fija, a mayor flexión el
// alcance vertical se acorta y el pie "flota" sobre el piso -- por eso la
// pierna delantera (baseAngle=0) recalcula su cadera para mantener el pie
// anclado en GROUND_Y (ver anchoredHipYFrontLeg más abajo). La pierna
// trasera conserva cadera fija (192) tal como antes de esta corrección.
function PiernaSegmento({
  cx,
  cy,
  baseAngle,
  kneeBend,
  opacity,
}: {
  cx: number;
  cy: number;
  baseAngle: number;
  kneeBend: number;
  opacity: number;
}) {
  return (
    <g transform={`translate(${cx},${cy}) rotate(${baseAngle})`} opacity={opacity}>
      <rect x={-LEG_W / 2} y={0} width={LEG_W} height={LEG_THIGH_LEN} rx={LEG_RX} fill="#2FBF71" />
      <g transform={`translate(0,${LEG_THIGH_LEN}) rotate(${kneeBend})`}>
        <rect x={-LEG_W / 2} y={0} width={LEG_W} height={LEG_SHIN_LEN} rx={LEG_RX} fill="#2FBF71" />
        <ellipse cx={0} cy={FOOT_OFFSET} rx={FOOT_RX} ry={FOOT_RY} fill="#2FBF71" />
      </g>
    </g>
  );
}

// Cadera necesaria para que, con baseAngle=0 (pierna delantera, muslo
// siempre vertical) y el kneeBend dado, el borde inferior del pie caiga
// exactamente en GROUND_Y -- en vez de rotar la pantorrilla desde una
// cadera fija (lo que despega el pie del piso a mayor flexión).
function anchoredHipYFrontLeg(kneeBend: number): number {
  const theta = (kneeBend * Math.PI) / 180;
  const footCenterOffsetY = LEG_THIGH_LEN + FOOT_OFFSET * Math.cos(theta);
  const footHalfHeight = Math.sqrt((FOOT_RX * Math.sin(theta)) ** 2 + (FOOT_RY * Math.cos(theta)) ** 2);
  return GROUND_Y - footCenterOffsetY - footHalfHeight;
}

export function RebaPiernasDiagram({
  condicion,
  mod,
}: {
  condicion: CondicionPiernas;
  mod: ModificadoresPiernas;
}) {
  const equilibrado = condicion === 'soporte_bilateral_simetrico';
  const kneeBend = KNEE_BEND[mod.flexionRodillas ?? 'ninguna'];
  const frontHipY = anchoredHipYFrontLeg(kneeBend);
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-35)" opacity={0.55}>
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#e2e8f0" />
        <g transform="translate(0,40) rotate(-25)">
          <rect x={-6} y={0} width={12} height={34} rx={6} fill="#e2e8f0" />
          <circle cx={0} cy={36} r={7} fill="#e2e8f0" />
        </g>
      </g>

      <PiernaSegmento cx={104.5} cy={192} baseAngle={equilibrado ? 0 : -32} kneeBend={kneeBend} opacity={0.55} />
      <PiernaSegmento cx={118.5} cy={frontHipY} baseAngle={0} kneeBend={kneeBend} opacity={1} />
      <line x1={60} y1={277} x2={165} y2={277} stroke="#94a3b8" strokeWidth={2} />

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={equilibrado ? 13 : 12} fontWeight={700} textAnchor="middle">{PIERNAS_LABEL[condicion]}</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={11} fontWeight={600} textAnchor="middle">{FLEXION_LABEL[mod.flexionRodillas ?? 'ninguna']}</text>
    </svg>
  );
}

// ===== Brazo =====
// Mismo patrón que RulaBrazoDiagram, incluyendo el "ghost" de
// silueta punteada para la categoría combinada (extensión >20° o
// flexión 20°-45°) que ilustra su ambigüedad, y el rectángulo de
// apoyo para el modificador que RESTA punto (puntoApoyoOFavorGravedad).

const BRAZO_ANGLE: Record<RangoBrazo, number> = {
  extension_20_a_flexion_20: -15,
  extension_mayor_20_o_flexion_20_45: -32,
  flexion_45_90: -70,
  flexion_mayor_90: -115,
};
const BRAZO_LABEL: Record<RangoBrazo, string> = {
  extension_20_a_flexion_20: 'extensión 20°–flexión 20°',
  extension_mayor_20_o_flexion_20_45: 'extensión >20° o flexión 20°–45°',
  flexion_45_90: 'flexión 45°–90°',
  flexion_mayor_90: 'flexión >90°',
};
const BRAZO_LABEL_SIZE: Record<RangoBrazo, number> = {
  extension_20_a_flexion_20: 14,
  extension_mayor_20_o_flexion_20_45: 10,
  flexion_45_90: 16,
  flexion_mayor_90: 16,
};
// eje/arco de brazo por rango (hombro fijo en 110,98) — mismos radios
// y convención "hacia abajo" que Brazo en RULA.
const BRAZO_ARC: Record<RangoBrazo, { line: [number, number]; arcD: string }> = {
  extension_20_a_flexion_20: { line: [123.5, 148.1], arcD: 'M 110 137 A 39 39 0 0 0 120.1 135.5' },
  extension_mayor_20_o_flexion_20_45: { line: [137.6, 142.1], arcD: 'M 110 137 A 39 39 0 0 0 130.7 131.1' },
  flexion_45_90: { line: [158.9, 115.8], arcD: 'M 110 137 A 39 39 0 0 0 146.6 111.3' },
  flexion_mayor_90: { line: [157.1, 76.0], arcD: 'M 110 137 A 39 39 0 0 0 145.3 81.5' },
};

export function RebaBrazoDiagram({ rango, mod }: { rango: RangoBrazo; mod: ModificadoresBrazo }) {
  const angle = BRAZO_ANGLE[rango];
  const { line, arcD } = BRAZO_ARC[rango];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      {rango === 'extension_mayor_20_o_flexion_20_45' && (
        <g transform="translate(110,98) scale(1.3) rotate(25)" opacity={0.4}>
          <rect x={-7} y={0} width={14} height={40} rx={7} fill="none" stroke="#94a3b8" strokeWidth={2.5} strokeDasharray="4 4" />
        </g>
      )}
      <g transform={`translate(110,98) scale(1.3) rotate(${angle})`}>
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#2FBF71" />
        <g transform="translate(0,40) rotate(-25)">
          <rect x={-6} y={0} width={12} height={34} rx={6} fill="#e2e8f0" opacity={0.75} />
          <circle cx={0} cy={36} r={7} fill="#e2e8f0" opacity={0.75} />
        </g>
      </g>
      <line x1={110} y1={98} x2={line[0]} y2={line[1]} stroke="#2FBF71" strokeWidth={2} strokeDasharray="3 3" opacity={0.9} />
      <path d={arcD} stroke="#f59e0b" strokeWidth={3} fill="none" />

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={BRAZO_LABEL_SIZE[rango]} fontWeight={700} textAnchor="middle">{BRAZO_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje brazo</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">eje tronco</text>

      {mod.hombroElevado && (
        <path d="M 30,64 L 30,44 M 24,50 L 30,44 L 36,50" stroke="#3b82f6" strokeWidth={3.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {mod.abducidoORotado && (
        <path d="M 17,95 L 43,95 M 23,89 L 17,95 L 23,101 M 37,89 L 43,95 L 37,101" stroke="#f59e0b" strokeWidth={3.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {mod.puntoApoyoOFavorGravedad && <rect x={18} y={126} width={24} height={8} rx={3} fill="#3b82f6" opacity={0.85} />}
    </svg>
  );
}

// ===== Antebrazo =====
// Sin modificadores en REBA — mismo patrón geométrico que
// RulaAntebrazoDiagram (brazo superior fijo/atenuado, antebrazo es el
// segmento medido), incluyendo el "ghost" para la categoría combinada
// (<60° o >100°).

const ANTEBRAZO_BASE: Record<RangoAntebrazo, { angle: number; ghost: number | null }> = {
  flexion_60_100: { angle: 80, ghost: null },
  flexion_menor_60_o_mayor_100: { angle: 110, ghost: 40 },
};
const ANTEBRAZO_LABEL: Record<RangoAntebrazo, string> = {
  flexion_60_100: 'flexión 60°–100°',
  flexion_menor_60_o_mayor_100: 'flexión <60° o >100°',
};

export function RebaAntebrazoDiagram({ rango }: { rango: RangoAntebrazo }) {
  const { angle: base, ghost } = ANTEBRAZO_BASE[rango];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-35)">
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#64748b" stroke="#94a3b8" strokeWidth={1} />
      </g>
      {ghost !== null && (
        <g transform={`translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(${ghost})`} opacity={0.4}>
          <rect x={-6} y={0} width={12} height={34} rx={6} fill="none" stroke="#94a3b8" strokeWidth={2} strokeDasharray="3 3" />
        </g>
      )}
      <g transform={`translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(${base})`}>
        <rect x={-6} y={0} width={12} height={34} rx={6} fill="#2FBF71" />
        <circle cx={0} cy={36} r={7} fill="#2FBF71" />
      </g>

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={rango === 'flexion_menor_60_o_mayor_100' ? 14 : 16} fontWeight={700} textAnchor="middle">{ANTEBRAZO_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje antebrazo</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">sin modificador</text>
    </svg>
  );
}

// ===== Muñeca =====
// Ángulos exagerados a propósito (mismo criterio aprobado para
// RulaMunecaDiagram) para que la diferencia entre rangos sea evidente.
// torsionODesviacion es un único modificador combinado (a diferencia
// de RULA, que distingue radial/cubital) — se representa con un único
// offset de rotación.

const MUNECA_BASE: Record<RangoMuneca, { angle: number; ghost: number | null }> = {
  neutra_o_flexion_extension_menor_15: { angle: 10, ghost: null },
  flexion_extension_mayor_15: { angle: 50, ghost: -50 },
};
const MUNECA_MOD_OFFSET = 30; // torsionODesviacion → +30 (misma magnitud que RULA)
const MUNECA_LABEL: Record<RangoMuneca, string> = {
  neutra_o_flexion_extension_menor_15: 'neutra o flexión/extensión <15°',
  flexion_extension_mayor_15: 'flexión/extensión >15°',
};

export function RebaMunecaDiagram({ rango, mod }: { rango: RangoMuneca; mod: ModificadoresMuneca }) {
  const { angle: base, ghost } = MUNECA_BASE[rango];
  const nested = base + (mod.torsionODesviacion ? MUNECA_MOD_OFFSET : 0);
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-35)">
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#64748b" stroke="#94a3b8" strokeWidth={1} />
      </g>
      <g transform="translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(80)">
        <rect x={-6} y={0} width={12} height={34} rx={6} fill="#94a3b8" />
      </g>
      {ghost !== null && (
        <g transform={`translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(80) translate(0,34) rotate(${ghost})`} opacity={0.4}>
          <rect x={-5} y={0} width={10} height={26} rx={4} fill="none" stroke="#94a3b8" strokeWidth={2} strokeDasharray="3 3" />
        </g>
      )}
      <g transform={`translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(80) translate(0,34) rotate(${nested})`}>
        <rect x={-5} y={0} width={10} height={26} rx={4} fill="#2FBF71" />
        <circle cx={0} cy={26} r={5} fill="#2FBF71" />
      </g>

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={rango === 'neutra_o_flexion_extension_menor_15' ? 12 : 14} fontWeight={700} textAnchor="middle">{MUNECA_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje muñeca</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">{mod.torsionODesviacion ? 'torsión o desviación' : 'ninguna'}</text>
    </svg>
  );
}
